'use client'

import {
  BottomSheet,
  Chip,
  ListHeader,
  Result,
  RightSelectRow,
  ScrollableChipGroup,
  SolidButton,
  TextButton,
  Image,
} from '@macarongfactory/web-ui'
import Link from 'next/link'
import { overlay } from 'overlay-kit'
import { useEffect, useRef } from 'react'
import { useInView } from 'react-intersection-observer'

import { CircularProgressIndicator } from '@/shared/design-system/components'
import { COMMON_ERROR_DESCRIPTION, COMMON_ERROR_TITLE } from '@/shared/lib'
import { ROUTES } from '@/shared/lib/routes'
import { OptimizedImage } from '@/shared/ui'

import {
  ALL_TOPIC_FILTER,
  TOPIC_SORT_OPTIONS,
  type ITopicContentItem,
  type ITopicFilter,
  type TTopicSort,
} from '../lib'
import CarStoryTabError from './CarStoryTabError'
import CarStoryTabLoading from './CarStoryTabLoading'
import useTrackOnVisible from '../model/useTrackOnVisible'

/**
 * 칩 영역 sticky top 위치.
 * TopNavigation(56px) + Tab(49px) 합산 — 상위 레이아웃 변경 시 함께 조정 필요
 */
const STICKY_TOP_OFFSET_PX = 105

/**
 * TopicContentsSection 의 트래킹 콜백 묶음.
 * 페이지마다 (drive / maintenance / ...) 다른 트래킹 함수를 주입하기 위해
 * 개별 prop drilling 대신 단일 객체로 전달.
 */
export interface ITopicContentsTracking {
  /** 정렬 필터 클릭 시 호출 */
  onSortClick?: (sortName: string) => void
  /** 칩 노출 시 호출 (각 칩마다 1회) */
  onChipShow?: (chipLabel: string) => void
  /** 칩 클릭 시 호출 */
  onChipClick?: (chipLabel: string) => void
  /** 칩 영역 첫 스크롤 시 호출 (1회) */
  onChipScroll?: () => void
  /** 카드 노출 시 호출 (1회) */
  onCardShow?: (item: ITopicContentItem, selectedFilter: string) => void
  /** 카드 클릭 시 호출 */
  onCardClick?: (item: ITopicContentItem, selectedFilter: string) => void
}

interface ITopicContentsSectionProps {
  filters: ITopicFilter[]
  items: ITopicContentItem[]
  /** 콘텐츠 목록 로딩 여부 (카드 영역 부분 로딩) */
  isContentsLoading?: boolean
  /** 콘텐츠 목록 에러 여부 (카드 영역 부분 에러) */
  isContentsError?: boolean
  /** 콘텐츠 목록 재요청 (부분 에러 재시도) */
  onContentsRetry?: () => void
  /** 현재 선택된 정렬 (상위에서 lift up) */
  selectedSort: TTopicSort
  /** 현재 선택된 카테고리 serverId (전체 = null, 상위에서 lift up) */
  selectedCategoryId: number | null
  /** 카테고리 변경 시 호출 (상위에서 categoryId 상태 + 쿼리 갱신) */
  onCategoryChange: (categoryId: number | null) => void
  /** 정렬 변경 시 호출 (상위에서 sort 상태 + 쿼리 갱신) */
  onSortChange: (sort: TTopicSort) => void
  /** 트래킹 콜백 묶음 (페이지별 트래킹 함수 주입) */
  tracking?: ITopicContentsTracking
  /** 인피니트 스크롤: 다음 페이지가 더 존재하는지 */
  hasNextPage?: boolean
  /** 인피니트 스크롤: 다음 페이지 로딩 중 */
  isFetchingNextPage?: boolean
  /** 인피니트 스크롤: 다음 페이지 로딩 실패 */
  isFetchNextPageError?: boolean
  /** 인피니트 스크롤: 다음 페이지 요청 */
  onFetchNextPage?: () => void
}

const TopicContentsSection = ({
  filters,
  items,
  isContentsLoading,
  isContentsError,
  onContentsRetry,
  selectedSort,
  selectedCategoryId,
  onCategoryChange,
  onSortChange,
  tracking,
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError,
  onFetchNextPage,
}: ITopicContentsSectionProps) => {
  const {
    onSortClick,
    onChipShow,
    onChipClick,
    onChipScroll,
    onCardShow,
    onCardClick,
  } = tracking ?? {}
  const selectedFilterLabel =
    filters.find(filter => filter.categoryId === selectedCategoryId)?.label ??
    ALL_TOPIC_FILTER

  const handleSelectSort = (sort: TTopicSort) => {
    onSortClick?.(sort)
    onSortChange(sort)
  }

  const handleSelectChip = (filter: ITopicFilter) => {
    onChipClick?.(filter.label)
    onCategoryChange(filter.categoryId)
  }

  const handleOpenSortSheet = () => {
    overlay.open(({ isOpen, close }) => (
      <BottomSheet
        open={isOpen}
        onClose={close}
        className='max-w-[480px] left-0 right-0 mx-auto'
        top={
          <BottomSheet.Top.LeftAlignedHeader
            title='콘텐츠 정렬'
            titleTypography='heading-3-bold'
          />
        }
        content={
          <BottomSheet.Content>
            <div className='pb-dimension-x5 flex flex-col'>
              {TOPIC_SORT_OPTIONS.map(sort => (
                <RightSelectRow
                  key={sort}
                  verticalPadding='dimension-x4'
                  title={
                    <RightSelectRow.Main.Title.Text
                      text={sort}
                      typography='title-1-medium'
                    />
                  }
                  right={
                    selectedSort === sort ? (
                      <RightSelectRow.Right.Checkmark />
                    ) : undefined
                  }
                  selected={selectedSort === sort}
                  clickable={() => {
                    handleSelectSort(sort)
                    close()
                  }}
                />
              ))}
            </div>
          </BottomSheet.Content>
        }
      />
    ))
  }

  return (
    <section className='pt-dimension-x3 pb-dimension-x6'>
      <ListHeader
        title={
          <ListHeader.Main.Title.Text
            text='주제별 콘텐츠'
            typography='heading-3-bold'
          />
        }
        right={
          <ListHeader.Main.Right.Accessory>
            <TextButton
              text={selectedSort}
              variant='chevron'
              chevron='ic-chevron-down'
              typography='body-1-medium'
              textColor='foreground-neutral-alternative'
              iconColor='foreground-neutral-alternative'
              clickable={handleOpenSortSheet}
            />
          </ListHeader.Main.Right.Accessory>
        }
      />
      <ChipScrollGroup
        filters={filters}
        selectedCategoryId={selectedCategoryId}
        onChipShow={onChipShow}
        onChipScroll={onChipScroll}
        onSelectChip={handleSelectChip}
      />
      {isContentsLoading && <CarStoryTabLoading fullScreen={false} />}
      {!isContentsLoading && isContentsError && (
        <CarStoryTabError
          fullScreen={false}
          onRetry={() => onContentsRetry?.()}
        />
      )}
      {!isContentsLoading && !isContentsError && (
        <>
          <ul>
            {items.map(item => (
              <li key={item.id}>
                <TopicContentRow
                  item={item}
                  selectedFilter={selectedFilterLabel}
                  onShow={onCardShow}
                  onClick={onCardClick}
                />
              </li>
            ))}
          </ul>
          <InfiniteScrollFooter
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={!!isFetchingNextPage}
            isFetchNextPageError={!!isFetchNextPageError}
            onFetchNextPage={onFetchNextPage}
          />
        </>
      )}
    </section>
  )
}

/**
 * 주제별 콘텐츠 인피니트 스크롤 하단 영역.
 * - 다음 페이지 존재 + 대기 상태: sentinel 활성 → 화면 진입 시 자동 fetch
 * - 다음 페이지 로딩 중: CircularProgressIndicator
 * - 다음 페이지 로딩 실패: Result + 다시 시도 버튼
 */
const InfiniteScrollFooter = ({
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError,
  onFetchNextPage,
}: {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isFetchNextPageError: boolean
  onFetchNextPage?: () => void
}) => {
  const { ref: sentinelRef } = useInView({
    rootMargin: '0px 0px 200px 0px',
    skip:
      !hasNextPage ||
      isFetchingNextPage ||
      isFetchNextPageError ||
      !onFetchNextPage,
    onChange: inView => {
      if (inView) onFetchNextPage?.()
    },
  })

  if (isFetchNextPageError) {
    return (
      <div className='py-dimension-x5 px-dimension-x5 flex w-full flex-col items-center justify-center'>
        <Result
          title={<Result.Title text={COMMON_ERROR_TITLE} />}
          description={<Result.Description text={COMMON_ERROR_DESCRIPTION} />}
          button={
            <SolidButton
              variant='neutral-weak'
              text='다시 시도하기'
              size='medium'
              clickable={() => onFetchNextPage?.()}
            />
          }
        />
      </div>
    )
  }

  if (isFetchingNextPage) {
    return (
      <div className='py-dimension-x2 flex w-full items-center justify-center'>
        <CircularProgressIndicator size='medium' variant='gray' />
      </div>
    )
  }

  if (hasNextPage) {
    return <div ref={sentinelRef} aria-hidden />
  }

  return null
}

const ChipScrollGroup = ({
  filters,
  selectedCategoryId,
  onChipShow,
  onChipScroll,
  onSelectChip,
}: {
  filters: ITopicFilter[]
  selectedCategoryId: number | null
  onChipShow?: (chipLabel: string) => void
  onChipScroll?: () => void
  onSelectChip: (filter: ITopicFilter) => void
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const hasScrolledRef = useRef(false)

  useEffect(
    function observeChipScrollOnce() {
      if (!onChipScroll) return
      const node = scrollContainerRef.current
      if (!node) return

      const handleScroll = () => {
        if (hasScrolledRef.current) return
        hasScrolledRef.current = true
        onChipScroll()
      }

      // ScrollableChipGroup 내부 스크롤 컨테이너에 위임
      node.addEventListener('scroll', handleScroll, {
        capture: true,
        passive: true,
      })
      return () => node.removeEventListener('scroll', handleScroll, true)
    },
    [onChipScroll]
  )

  return (
    <div
      ref={scrollContainerRef}
      className='bg-background-surface-default sticky z-30'
      style={{ top: `${STICKY_TOP_OFFSET_PX}px` }}
    >
      <ScrollableChipGroup>
        {filters.map(filter => (
          <ChipItem
            key={filter.categoryId ?? ALL_TOPIC_FILTER}
            label={filter.label}
            selected={selectedCategoryId === filter.categoryId}
            onShow={onChipShow}
            onClick={() => onSelectChip(filter)}
          />
        ))}
      </ScrollableChipGroup>
    </div>
  )
}

const ChipItem = ({
  label,
  selected,
  onShow,
  onClick,
}: {
  label: string
  selected: boolean
  onShow?: (chipLabel: string) => void
  onClick: () => void
}) => {
  const chipRef = useRef<HTMLDivElement | null>(null)
  useTrackOnVisible({
    ref: chipRef,
    tracker: () => onShow?.(label),
    enabled: !!onShow,
  })

  return (
    <div ref={chipRef}>
      <Chip.Filter
        text={label}
        variant='outline'
        trailingIcon='none'
        selected={selected}
        clickable={onClick}
      />
    </div>
  )
}

const TopicContentRow = ({
  item,
  selectedFilter,
  onShow,
  onClick,
}: {
  item: ITopicContentItem
  selectedFilter: string
  onShow?: (item: ITopicContentItem, selectedFilter: string) => void
  onClick?: (item: ITopicContentItem, selectedFilter: string) => void
}) => {
  const rowRef = useRef<HTMLAnchorElement | null>(null)
  useTrackOnVisible({
    ref: rowRef,
    tracker: () => onShow?.(item, selectedFilter),
    enabled: !!onShow,
  })

  return (
    <Link
      ref={rowRef}
      href={ROUTES.CAR_STORY.CONTENT_DETAIL(item.id)}
      onClick={() => onClick?.(item, selectedFilter)}
      className='gap-dimension-x3 px-dimension-x5 py-dimension-x4 flex h-22 items-center'
    >
      <div className='rounded-dimension-x2 bg-background-neutral-weak w-dimension-x16 relative aspect-square shrink-0 overflow-hidden'>
        <OptimizedImage
          src={item.imageUrl}
          alt={item.title}
          width='100%'
          height='100%'
          radius={0}
          scaletype='crop'
          sizes='64px'
          fallback={<Image.Fallback />}
        />
      </div>
      <p className='text-title-2-semibold text-foreground-neutral-strong line-clamp-2 flex-1 whitespace-pre-line'>
        {item.title}
      </p>
    </Link>
  )
}

export default TopicContentsSection
